import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bike, Image, Upload, X, Lock } from "lucide-react";
import useFeatureAccess from "@/hooks/useFeatureAccess";

export const BrandingManagement = () => {
  const { appName, logoBase64, updateBranding, refreshBranding } = useBranding();
  const { canSaveFeature } = useFeatureAccess();
  const { canSave, reason } = canSaveFeature("branding");
  
  const [newAppName, setNewAppName] = useState(appName);
  const [previewLogo, setPreviewLogo] = useState(logoBase64);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setNewAppName(appName);
    setPreviewLogo(logoBase64);
  }, [appName, logoBase64]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewLogo(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!canSave) {
      toast.error(reason || "This feature is not available");
      return;
    }
    if (!newAppName.trim() || newAppName.trim().length < 2) {
      toast.error("App name must be at least 2 characters");
      return;
    }

    setSaving(true);
    try {
      await updateBranding(newAppName.trim(), previewLogo);
      toast.success("Branding updated successfully!");
      refreshBranding();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update branding");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = newAppName !== appName || previewLogo !== logoBase64;

  return (
    <div data-testid="branding-management">
      <h1 className="text-2xl font-bold text-foreground mb-2">Branding Management</h1>
      <p className="text-muted-foreground mb-6">
        Customize your app's name and logo. Changes apply instantly across all pages.
      </p>

      {/* Restriction Banner */}
      {!canSave && reason && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{reason}</p>
            <p className="text-sm text-amber-600">You can preview changes but cannot save them.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label htmlFor="app-name" className="text-base font-semibold">App Name</Label>
            <p className="text-sm text-muted-foreground mt-1">
              This name appears in the header across all pages
            </p>
          </div>
          <Input
            id="app-name"
            value={newAppName}
            onChange={(e) => setNewAppName(e.target.value)}
            placeholder="Enter app name"
            className="h-12 text-lg"
            maxLength={30}
            data-testid="app-name-input"
          />
          <p className="text-xs text-muted-foreground">{newAppName.length}/30 characters</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold">App Logo</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a logo (PNG, JPG, or SVG, max 2MB)
            </p>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/50">
              {previewLogo ? (
                <img 
                  src={previewLogo} 
                  alt="Logo preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center">
                  <Image className="w-8 h-8 text-muted-foreground mx-auto mb-1" />
                  <span className="text-xs text-muted-foreground">No logo</span>
                </div>
              )}
            </div>
            
            <div className="flex-1 space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Logo
              </Button>
              {previewLogo && (
                <Button
                  variant="ghost"
                  onClick={handleRemoveLogo}
                  className="w-full text-destructive hover:text-destructive"
                  data-testid="remove-logo-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove Logo
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 p-6 bg-muted/50 rounded-xl border border-border">
        <h3 className="font-semibold text-foreground mb-4">Preview</h3>
        <div className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
          {previewLogo ? (
            <img src={previewLogo} alt="Preview" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Bike className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h2 className="font-bold text-foreground">{newAppName || "App Name"}</h2>
            <p className="text-xs text-muted-foreground">Your tagline here</p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges || !canSave}
          className="min-w-32"
          data-testid="save-branding-btn"
        >
          {!canSave && <Lock className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default BrandingManagement;
